import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { asyncRpcQuery } from 'ducks/ai/queries'
import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'
import {
  doAiRequest,
  doCancelAiResult,
  doHidePrompt,
  doClearState,
  getAiType,
  doFetchJobResult,
  doAiFailed,
  doAiEnd,
} from './operations'
import { findJobId } from './utils'
import { selectAiComposerRequest } from './selectors/selectAiComposerRequest'
import { selectAiInstantReply } from './selectors/selectAiInstantReply'
import { COMPOSER_TYPE, INSTANT_REPLY_TYPE } from './types'

const TIMEOUT = 1000

// HACK (jscheel): this is bad, we need to refactor to just have a request id that drives everything in the state
export const AI_REQUEST_TYPE_SELECT_MAP = {
  [COMPOSER_TYPE]: selectAiComposerRequest,
  [INSTANT_REPLY_TYPE]: selectAiInstantReply,
}

export const useAi = aiRequestType => {
  const dispatch = useDispatch()
  const resultCheckTimeoutRef = useRef(null)
  const requestAborted = useRef({ current: false })
  const resolveWaitRef = useRef(null)
  const requestState = useSelector(AI_REQUEST_TYPE_SELECT_MAP[aiRequestType])
  const ticketId = useSelector(selectCurrentTicketId)
  useEffect(
    () => () => {
      requestAborted.current.current = true
      clearTimeout(resultCheckTimeoutRef.current)
      if (resolveWaitRef.current) resolveWaitRef.current()
    },
    [ticketId]
  )

  // FIXME (jscheel && kevinrademan && jdarnok): As discussed, this function
  // needs heavy refactoring so that its logic lives in operations. The hook
  // should ultimate just expose these operations. In the interest of time, we
  // are not full refactoring for now (save for the tweaks in the PR that also
  // added this note).
  const requestAndWait = useCallback(
    async (variables, options = {}) => {
      const mergedOptions = { ...options, requestType: aiRequestType }
      let retVal
      requestAborted.current.current = true
      if (resolveWaitRef.current) resolveWaitRef.current()
      const request = { current: false }
      requestAborted.current = request

      try {
        const result = await dispatch(
          doAiRequest(asyncRpcQuery, variables, mergedOptions)
        )
        if (request.current) {
          return undefined
        }
        const resultJobId = findJobId(result)
        const pollForResult = async () => {
          const jobResult = await dispatch(
            doFetchJobResult(resultJobId, mergedOptions)
          )
          if (request.current) {
            return undefined
          }
          const { state, value } = jobResult?.ephemeralJobResult || {}
          if (state === 'COMPLETED') {
            return value
          }
          if (state === 'FAILED' || state === undefined) {
            throw new Error('Job failed')
          }
          await new Promise(resolve => {
            clearTimeout(resultCheckTimeoutRef.current)
            resolveWaitRef.current = resolve
            resultCheckTimeoutRef.current = setTimeout(resolve, TIMEOUT)
          })
          if (request.current) {
            return undefined
          }
          const ret = await pollForResult()
          if (request.current) {
            return undefined
          }
          return ret
        }
        retVal = await pollForResult()
      } catch (e) {
        if (request.current) return undefined
        dispatch(doAiFailed(mergedOptions.requestType))
        throw e // If you want to notify the caller about the failure
      }
      if (!request.current) {
        dispatch(
          doAiEnd(
            getAiType(mergedOptions.requestType),
            variables,
            mergedOptions,
            retVal
          )
        )
      }
      return retVal
    },
    [dispatch, aiRequestType, requestAborted]
  )

  const rateResponse = useCallback(() => {
    // TODO: Need to wire this up to some backend rating mechanisim
  }, [])

  useEffect(
    () => {
      const timeoutId = resultCheckTimeoutRef.current
      return () => {
        clearTimeout(timeoutId)
      }
    },
    [resultCheckTimeoutRef]
  )

  const abortResult = useCallback(
    () => {
      requestAborted.current.current = true
      clearTimeout(resultCheckTimeoutRef.current)
      if (resolveWaitRef.current) resolveWaitRef.current()
      dispatch(doCancelAiResult(aiRequestType))
    },
    [dispatch, aiRequestType]
  )

  const hidePrompt = useCallback(
    () => {
      dispatch(doHidePrompt(aiRequestType))
    },
    [dispatch, aiRequestType]
  )

  const clearState = useCallback(
    () => {
      dispatch(doClearState(aiRequestType))
    },
    [dispatch, aiRequestType]
  )

  const cancelRequest = useCallback(
    () => {
      clearTimeout(resultCheckTimeoutRef.current)
      requestAborted.current.current = true
      if (resolveWaitRef.current) resolveWaitRef.current()
      resolveWaitRef.current = null
      resultCheckTimeoutRef.current = null
      dispatch(doClearState(aiRequestType))
    },
    [resultCheckTimeoutRef, requestAborted, dispatch, aiRequestType]
  )

  const retryRequest = useCallback(
    async () => {
      const lastRequest = requestState.lastRequest
      if (!lastRequest || !lastRequest.variables || !lastRequest.options) {
        throw new Error('Tried to retry without a proper lastRequest state')
      }

      return requestAndWait(lastRequest.variables, lastRequest.options)
    },
    [requestState, requestAndWait]
  )

  useEffect(
    () => {
      // If Change ticket before the request is finished, the state is cleared
      // but request is still processing, abort the request
      if (
        !requestState.loading &&
        !requestState.loaded &&
        !requestState.error &&
        resultCheckTimeoutRef.current
      ) {
        cancelRequest()
      }
    },
    [
      cancelRequest,
      requestState.loading,
      requestState.loaded,
      requestState.error,
    ]
  )

  return {
    requestAndWait,
    rateResponse,
    abortResult,
    hidePrompt,
    clearState,
    cancelRequest,
    retryRequest,
    requestState,
  }
}
